import { Grid, Paper, Skeleton } from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { generateRainbowColors, CustomTooltip } from "../../home";
import {
  getConversationPopularity,
  getFeedback,
} from "modules/agentMySecurity/services/embeddingsService";
import { RotatedTick } from "core/components/global/tables/rechart/helpers";
import { useEffect, useState } from "react";
import { CardHeader } from "../dashboard.styled";
import { useAppTheme } from "core/contexts/theme/theme";
import AdminIaus from "./iaus";
import { v4 as uuid4 } from "uuid";


// Model Performance Tab
export const ModelPerformanceTab = () => {
  const { apptheme } = useAppTheme();
  const [loadingFeedback, setIsFeedbackLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [loadingModels, setAreModelsLoading] = useState(true);
  const [models, setModels] = useState([]);
  const [colors, setColors] = useState([]);

  // Fetch data from backend
  useEffect(() => {
    /**
     * Fetch all the data needed for the two charts in this component
     * The following IAU table will fetch its own table data to keep rendering separate
     */
    const fetchData = async () => {
      try {
        // Fetch and Transform the feedback data for the Positive/Negative Per Model Chart
        let feedbackData = await getFeedback();
        const aggregatedFeedback = feedbackData.reduce((result, item) => {
          const model = item.model;
          if (!result[model]) {
            result[model] = { positive: 0, negative: 0 };
          }
          if (item.negative) {
            result[model].negative++;
          } else {
            result[model].positive++;
          }
          return result;
        }, {});
        const formattedFeedback: any = Object.entries(aggregatedFeedback).map(
          ([model, counts]: any) => ({
            model,
            positive: counts.positive,
            negative: counts.negative,
          })
        );
        setFeedback(formattedFeedback);

        // Fetch the color range as a rainbow
        const rainbow: any = generateRainbowColors(formattedFeedback.length);
        setColors(rainbow);

        // Fetch and transform the model data for the popularity chart
        const modelUsageData = await getConversationPopularity();
        const transformedData = modelUsageData.map((item) => {
          const dataPoint = { date: item._id };
          item.models.forEach((model) => {
            dataPoint[model.model] = model.count;
          });
          return dataPoint;
        });
        setModels(transformedData);

        // Stop the loading animation
        setIsFeedbackLoading(false);
        setAreModelsLoading(false);
      } catch (error) {
        // Stop the loading animation
        setIsFeedbackLoading(false);
        setAreModelsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Grid container spacing={3}>

      {/* IAU Usage Trend for the Past Month */}
      <Grid item xs={12} md={12} className="hide-for-small-screens">
        <Paper className="p-2">
          <CardHeader>Trends in Model Usage Initiated by New Conversations</CardHeader>
          {loadingModels ? (
            <Skeleton variant="rectangular" height={600} animation="wave" />
          ) : (
            <ResponsiveContainer width="100%" height={600}>
              <LineChart
                data={models}
                margin={{ top: 40, right: 20, left: 20, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  interval={0}
                  dataKey="date"
                  tick={<RotatedTick fill={apptheme.colors.textColor} />}
                />
                <YAxis
                  label={{
                    value: "Conversations Started",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                />
                <Legend wrapperStyle={{ bottom: 10 }} />

                {/* Dynamically render lines for each model */}
                {Array.from(
                  new Set(
                    models
                      .flatMap((d) => Object.keys(d))
                      .filter((k) => k !== "date")
                  )
                ).map((model: any, index) => (
                  <Line
                    key={uuid4()}
                    type="monotone"
                    dataKey={model}
                    stroke={colors[index % colors.length]}
                    activeDot={{ r: 8 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      {/* IAU Feedback */}
      <Grid item xs={12} md={12}>
        <Paper className="p-2">
          <CardHeader>Feedback Trends: Positive vs. Negative</CardHeader>
          {loadingFeedback ? (
            <Skeleton variant="rectangular" height={600} animation="wave" />
          ) : (
            <ResponsiveContainer width="100%" height={600}>
              <BarChart
                data={feedback}
                margin={{ top: 40, right: 20, left: 20, bottom: 200 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  interval={0}
                  dataKey="model"
                  tick={<RotatedTick fill={apptheme.colors.textColor} />}
                />
                <YAxis
                  label={{
                    value: "Feedback Count",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: apptheme.colors.barChartBarFullWBgHv }}
                />
                <Legend wrapperStyle={{ bottom: 10 }} />
                <Bar
                  dataKey="positive"
                  stackId="a"
                  name="Positive Feedback"
                  fill={apptheme.colors.barChartBarBg}
                  radius={[0, 4, 4, 0]}
                />
                <Bar
                  dataKey="negative"
                  stackId="a"
                  name="Negative Feedback"
                  fill={apptheme.colors.highlight3}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Grid>

      {/* IAUs Table */}
      <Grid item xs={12}>
        <AdminIaus />
      </Grid>
    </Grid>
  );
};
